import {
  Box, Chip, Divider, InputAdornment, Link, ListItemButton, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FixedSizeList } from 'react-window'
import PageModal from 'new-components/modal/page-modal'
import InputCheckbox from 'component/Input/InputCheckbox'
import type { PromotionRef } from 'features/executive/shared/hooks/use-reference-data'

interface PromotionPickerModalProps {
  open: boolean
  onClose: () => void
  /** Final list of promotion IDs for the bucket (replaces existing). */
  onConfirm: (selectedIds: number[]) => void
  promotions: PromotionRef[]
  /** Promotions already in the current bucket — pre-checked and mutable. */
  currentSelectedIds: number[]
  /** Promotions claimed by *other* buckets — shown as disabled (cannot toggle). */
  claimedByOthersIds: number[]
  /** Bucket label, surfaced in the modal title for context. */
  bucketLabel?: string
}

type ListItem = { kind: 'divider' } | { kind: 'option'; row: PromotionRef }

export default function PromotionPickerModal(props: PromotionPickerModalProps) {
  const { open, onClose, onConfirm, promotions, currentSelectedIds, claimedByOthersIds, bucketLabel } = props
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(handle)
  }, [search])

  // Seed selection from the bucket's current ids every time the modal opens,
  // and reset transient state when it closes.
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(currentSelectedIds))
      setSearch('')
      setDebouncedSearch('')
      setShowSelectedOnly(false)
    }
  }, [open])

  const claimedSet = useMemo(() => new Set(claimedByOthersIds), [claimedByOthersIds])

  const filteredMatches = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase()
    const list = q
      ? promotions.filter((p) => p.name.toLowerCase().includes(q))
      : promotions.slice()
    return list.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
    )
  }, [promotions, debouncedSearch])

  /** Pinned items are currently-checked; unclaimed-and-unchecked fall below the divider. */
  const listItems: ListItem[] = useMemo(() => {
    const picked = filteredMatches.filter((p) => selectedIds.has(p.id))
    if (showSelectedOnly) {
      return picked.map((row) => ({ kind: 'option' as const, row }))
    }
    const rest = filteredMatches.filter((p) => !selectedIds.has(p.id))
    const items: ListItem[] = picked.map((row) => ({ kind: 'option' as const, row }))
    if (picked.length > 0 && rest.length > 0) items.push({ kind: 'divider' })
    rest.forEach((row) => items.push({ kind: 'option', row }))
    return items
  }, [filteredMatches, selectedIds, showSelectedOnly])

  const toggle = (id: number) => {
    if (claimedSet.has(id)) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** Select every filtered promotion that isn't claimed by another bucket. */
  const selectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      filteredMatches.forEach((p) => {
        if (!claimedSet.has(p.id)) next.add(p.id)
      })
      return next
    })
  }

  /** Drop every filtered selection (claimed items aren't in the set anyway). */
  const clearFilteredSelection = () => {
    const filteredIds = new Set(filteredMatches.map((p) => p.id))
    setSelectedIds((prev) => {
      const next = new Set<number>()
      prev.forEach((id) => {
        if (!filteredIds.has(id)) next.add(id)
      })
      return next
    })
  }

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds))
  }

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = listItems[index]
    if (!item) return null
    if (item.kind === 'divider') {
      return (
        <Box style={style} sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
          <Divider sx={{ width: '100%' }} />
        </Box>
      )
    }
    const p = item.row
    const isClaimed = claimedSet.has(p.id)
    const isChecked = selectedIds.has(p.id)
    return (
      <ListItemButton
        style={style}
        disabled={isClaimed}
        onClick={() => toggle(p.id)}
        sx={{ pl: 1, pr: 2 }}
      >
        <InputCheckbox checked={isChecked} disabled={isClaimed} />
        <Typography sx={{ flexGrow: 1, mr: 1 }} noWrap>
          {p.name}
        </Typography>
        {isClaimed && (
          <Chip
            size="small"
            label={t('DASHBOARD_TARGETS.PROMO.PICKER.CLAIMED_BY_OTHER', { defaultValue: 'อยู่กลุ่มอื่น' })}
          />
        )}
      </ListItemButton>
    )
  }

  const selectableMatchCount = filteredMatches.filter((p) => !claimedSet.has(p.id)).length

  const title = bucketLabel
    ? `${t('DASHBOARD_TARGETS.PROMO.PICKER.TITLE', { defaultValue: 'เลือกโปรโมชั่นที่ต้องการนับรวม' })} — ${bucketLabel}`
    : (t('DASHBOARD_TARGETS.PROMO.PICKER.TITLE', { defaultValue: 'เลือกโปรโมชั่นที่ต้องการนับรวม' }) as string)

  return (
    <PageModal
      isOpen={open}
      size="M"
      title={title}
      handleClose={onClose}
      onCancel={{
        onClick: onClose,
        title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
      }}
      onSubmit={{
        onClick: handleConfirm,
        title: t('DASHBOARD_TARGETS.PROMO.PICKER.CONFIRM_N', {
          defaultValue: 'ยืนยัน ({{n}})',
          n: selectedIds.size,
        }) as string,
        width: 180,
      }}
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={
          t('DASHBOARD_TARGETS.PROMO.PICKER.SEARCH_PLACEHOLDER', { defaultValue: 'ค้นหาโปรโมชั่น…' }) as string
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <InputCheckbox
          checked={showSelectedOnly}
          onChange={(e: any) => setShowSelectedOnly(Boolean(e.target.checked))}
          label={t('DASHBOARD_TARGETS.PICKER.SHOW_SELECTED_ONLY', { defaultValue: 'แสดงเฉพาะที่เลือก' })}
        />
        <Box sx={{ flexGrow: 1 }} />
        {selectableMatchCount > 0 && (
          <Link component="button" variant="caption" onClick={selectAllFiltered}>
            {t('DASHBOARD_TARGETS.PICKER.SELECT_ALL_FILTERED', {
              defaultValue: 'เลือกทั้งหมดที่กรอง ({{n}})',
              n: selectableMatchCount,
            })}
          </Link>
        )}
        {selectedIds.size > 0 && (
          <Link component="button" variant="caption" color="text.secondary" onClick={clearFilteredSelection}>
            {t('DASHBOARD_TARGETS.PICKER.CLEAR_FILTERED', { defaultValue: 'ล้างที่เลือก' })}
          </Link>
        )}
      </Box>
      {selectedIds.size > 0 && (
        <Box sx={{ mb: 1 }}>
          <Chip
            color="primary"
            size="small"
            label={t('DASHBOARD_TARGETS.PICKER.SELECTED_COUNT', {
              defaultValue: 'เลือกแล้ว {{n}} รายการ',
              n: selectedIds.size,
            })}
          />
        </Box>
      )}
      {listItems.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {showSelectedOnly
              ? t('DASHBOARD_TARGETS.PICKER.EMPTY_SELECTED', { defaultValue: 'ยังไม่มีรายการที่เลือก' })
              : t('DASHBOARD_TARGETS.PROMO.PICKER.EMPTY', {
                  defaultValue: 'ไม่พบโปรโมชั่นที่ตรงกับ "{{q}}"',
                  q: debouncedSearch,
                })}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: '50vh' }}>
          <FixedSizeList
            height={Math.floor(window.innerHeight * 0.5)}
            width="100%"
            itemSize={44}
            itemCount={listItems.length}
            overscanCount={6}
          >
            {Row}
          </FixedSizeList>
        </Box>
      )}
    </PageModal>
  )
}
